import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FaRocket, FaLock, FaBolt, FaTools } from 'react-icons/fa';
import Footer from './Footer';
import './Register.css';

const appname = process.env.REACT_APP_APPLICATION_NAME || 'Innovatix Conversion Hub';

const Register = () => {
	const [formData, setFormData] = useState({
		firstName: '',
		lastName: '',
		email: '',
		phoneNumber: '',
		username: '',
		password: '',
		role: 'user',
		department: ''
	});
	const [errorMessage, setErrorMessage] = useState('');
	const [fieldErrors, setFieldErrors] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [showOTPForm, setShowOTPForm] = useState(false);
	const [otp, setOtp] = useState('');
	const [otpEmail, setOtpEmail] = useState('');
	const [isVerifying, setIsVerifying] = useState(false);
	const [isResending, setIsResending] = useState(false);
	const [successMessage, setSuccessMessage] = useState('');
	const navigate = useNavigate();

	// Email validation regex
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	
	// Phone validation regex (basic international format)
	const phoneRegex = /^[\+]?[\s\-\(\)]?[0-9]{10,14}$/;

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
		
		// Clear field-specific error when user starts typing
		if (fieldErrors[name]) {
			setFieldErrors({ ...fieldErrors, [name]: '' });
		}
		
		// Clear general error message
		if (errorMessage) {
			setErrorMessage('');
		}
	};

	const validateForm = () => {
		const errors = {};
		
		// Required field validation
		if (!formData.firstName.trim()) {
			errors.firstName = 'First name is required';
		}
		
		if (!formData.lastName.trim()) {
			errors.lastName = 'Last name is required';
		}
		
		if (!formData.email.trim()) {
			errors.email = 'Email is required';
		} else if (!emailRegex.test(formData.email)) {
			errors.email = 'Please enter a valid email address';
		}
		
		if (!formData.username.trim()) {
			errors.username = 'Username is required';
		} else if (formData.username.length < 3) {
			errors.username = 'Username must be at least 3 characters long';
		}
		
		if (!formData.password.trim()) {
			errors.password = 'Password is required';
		} else if (formData.password.length < 6) {
			errors.password = 'Password must be at least 6 characters long';
		}
		
		// Optional field validation
		if (formData.phoneNumber && !phoneRegex.test(formData.phoneNumber.replace(/\s/g, ''))) {
			errors.phoneNumber = 'Please enter a valid phone number';
		}

		setFieldErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleRegister = async (e) => {
		e.preventDefault();
		
		// Validate form
		if (!validateForm()) {
			setErrorMessage('Please fix the errors below and try again.');
			return;
		}

		setIsLoading(true);
		setErrorMessage('');
		setSuccessMessage('');

		try {
			console.log('Attempting registration with data:', {
				...formData,
				password: '[HIDDEN]'
			});

			const response = await axios.post(`${API_BASE_URL}/auth/register`, formData);
			console.log('Registration OTP sent:', response.data);
			
			// Show OTP form
			setOtpEmail(formData.email);
			setShowOTPForm(true);
			setSuccessMessage(response.data.message || 'OTP sent to your email. Please check your inbox.');
			setErrorMessage('');
			
			// Show OTP in console for development (if returned)
			if (response.data.otp && (process.env.NODE_ENV === 'development' || response.data.warning)) {
				console.log('OTP (development only):', response.data.otp);
			}
		} catch (error) {
			console.error('Registration error:', error);
			
			if (error.response?.data?.message) {
				setErrorMessage(error.response.data.message);
			} else if (error.response?.status === 400) {
				setErrorMessage('Registration failed. Please check your information and try again.');
			} else if (error.code === 'ECONNREFUSED') {
				setErrorMessage('Cannot connect to server. Please check if the backend is running.');
			} else if (error.code === 'NETWORK_ERROR') {
				setErrorMessage('Network error. Please check your internet connection.');
			} else {
				setErrorMessage('Registration failed. Please try again later.');
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleVerifyOTP = async (e) => {
		e.preventDefault();
		
		if (!otp || otp.length !== 6) {
			setErrorMessage('Please enter a valid 6-digit OTP code.');
			return;
		}

		setIsVerifying(true);
		setErrorMessage('');
		setSuccessMessage('');

		try {
			const response = await axios.post(`${API_BASE_URL}/auth/verify-otp`, {
				email: otpEmail,
				otp: otp
			});
			
			console.log('OTP verification successful:', response.data);
			
			// Show success message and redirect to login
			setSuccessMessage('Registration successful! Redirecting to login...');
			setTimeout(() => {
				navigate('/login', { 
					state: { 
						message: 'Registration successful! Please log in with your credentials.',
						type: 'success'
					}
				});
			}, 2000);
		} catch (error) {
			console.error('OTP verification error:', error);
			
			if (error.response?.data?.message) {
				setErrorMessage(error.response.data.message);
			} else if (error.response?.status === 400) {
				setErrorMessage('Invalid OTP. Please check and try again.');
			} else {
				setErrorMessage('OTP verification failed. Please try again.');
			}
		} finally {
			setIsVerifying(false);
		}
	};

	const handleResendOTP = async () => {
		setIsResending(true);
		setErrorMessage('');
		setSuccessMessage('');

		try {
			const response = await axios.post(`${API_BASE_URL}/auth/resend-otp`, {
				email: otpEmail
			});
			
			console.log('OTP resent:', response.data);
			setSuccessMessage(response.data.message || 'New OTP sent to your email. Please check your inbox.');
			
			// Show OTP in console for development (if returned)
			if (response.data.otp && (process.env.NODE_ENV === 'development' || response.data.warning)) {
				console.log('New OTP (development only):', response.data.otp);
			}
		} catch (error) {
			console.error('Resend OTP error:', error);
			
			if (error.response?.data?.message) {
				setErrorMessage(error.response.data.message);
			} else {
				setErrorMessage('Failed to resend OTP. Please try again.');
			}
		} finally {
			setIsResending(false);
		}
	};

	const handleOtpChange = (e) => {
		const value = e.target.value.replace(/\D/g, '').slice(0, 6); // Only numbers, max 6 digits
		setOtp(value);
		if (errorMessage) {
			setErrorMessage('');
		}
	};

	return (
		<div className="register-page">
			<div className="register-background" aria-hidden="true" />
			<Container fluid className="register-container">
				<Row className="g-0 register-row">
					{/* Left: Why Choose panel (dark theme) */}
					<Col xs={12} lg={5} className="register-features-panel order-2 order-lg-0">
						<div className="register-features-content">
							<div className="register-features-logo">
								<img src="/logonew.png" alt="Continuum Logo" className="register-logo-image" />
							</div>
							<div className="register-features-header">
								<h1 className="register-features-title">Why Choose {appname}?</h1>
								<div className="register-features-divider" />
							</div>
							<div className="register-features-list">
								<div className="register-feature-item">
									<div className="register-feature-icon"><FaRocket /></div>
									<div className="register-feature-content">
										<h3 className="register-feature-title">AI-Powered Solutions</h3>
										<p className="register-feature-desc">Leverage cutting-edge AI for test case generation, BRD to FRD conversion, and intelligent bug reporting.</p>
									</div>
								</div>
								<div className="register-feature-item">
									<div className="register-feature-icon"><FaLock /></div>
									<div className="register-feature-content">
										<h3 className="register-feature-title">Secure & Reliable</h3>
										<p className="register-feature-desc">Enterprise-grade security with role-based access and secure data handling.</p>
									</div>
								</div>
								<div className="register-feature-item">
									<div className="register-feature-icon"><FaBolt /></div>
									<div className="register-feature-content">
										<h3 className="register-feature-title">Faster Development</h3>
										<p className="register-feature-desc">Reduce development time with automated test generation and documentation conversion.</p>
									</div>
								</div>
								<div className="register-feature-item">
									<div className="register-feature-icon"><FaTools /></div>
									<div className="register-feature-content">
										<h3 className="register-feature-title">Comprehensive Tools</h3>
										<p className="register-feature-desc">QA automation suite including Selenium scripts, API testing, and test data generation.</p>
									</div>
								</div>
							</div>
						</div>
					</Col>

					{/* Right: Registration or OTP Form (white panel) */}
					<Col xs={12} lg={7} className="register-form-panel order-1 order-lg-0">
						<div className="register-form-inner">
							<h2 className="register-form-title">
								{showOTPForm ? 'Verify Your Email' : 'Create your account'}
							</h2>

									{showOTPForm ? (
										// OTP Verification Form
										<Form onSubmit={handleVerifyOTP} className="d-flex flex-column h-100">
											{successMessage && (
												<Alert variant="success" className="mb-3">
													{successMessage}
												</Alert>
											)}
											{errorMessage && (
												<Alert variant="danger" className="mb-3">
													{errorMessage}
												</Alert>
											)}
											
											<div className="flex-grow-1">
												<Alert variant="info" className="mb-4 register-alert-info">
													<p className="mb-2"><strong>We've sent a verification code to:</strong></p>
													<p className="mb-0 register-otp-email">{otpEmail}</p>
													<p className="mb-0 mt-2 register-otp-hint">Please check your email and enter the 6-digit code below.</p>
												</Alert>

												<Form.Group className="mb-4">
													<Form.Label className="register-label">Enter Verification Code</Form.Label>
													<Form.Control 
														type="text"
														value={otp} 
														onChange={handleOtpChange}
														placeholder="000000"
														maxLength={6}
														required
														className="register-input register-otp-input"
													/>
													<Form.Text className="text-muted">
														Enter the 6-digit code sent to your email
													</Form.Text>
												</Form.Group>
											</div>
											
											<div className="d-flex flex-column gap-3 mt-4">
												<Button 
													type="submit"
													variant="primary" 
													size="lg" 
													disabled={isVerifying || otp.length !== 6}
													className="register-btn register-btn-primary py-3 fw-bold"
												>
													{isVerifying ? (
														<>
															<Spinner animation="border" size="sm" className="me-2" />
															Verifying...
														</>
													) : (
														'Verify & Complete Registration'
													)}
												</Button>
												
												<Button 
													type="button"
													variant="outline-secondary" 
													size="lg"
													onClick={handleResendOTP}
													disabled={isResending}
													className="register-btn register-btn-outline-secondary"
												>
													{isResending ? (
														<>
															<Spinner animation="border" size="sm" className="me-2" />
															Sending...
														</>
													) : (
														'Resend OTP'
													)}
												</Button>

												<Button 
													type="button"
													variant="outline-primary" 
													onClick={() => {
														setShowOTPForm(false);
														setOtp('');
														setErrorMessage('');
														setSuccessMessage('');
													}}
													size="lg"
													className="register-btn register-btn-outline"
												>
													Back to Registration
												</Button>
											</div>
										</Form>
									) : (
										// Registration Form
										<Form onSubmit={handleRegister} className="d-flex flex-column h-100">
											{successMessage && (
												<Alert variant="success" className="mb-3">
													{successMessage}
												</Alert>
											)}
											{errorMessage && (
												<Alert variant="danger" className="mb-3">
													{errorMessage}
												</Alert>
											)}
										
										<div className="flex-grow-1">
											<Row className="g-3">
											<Col xs={12} sm={6}>
												<Form.Group>
													<Form.Label className="register-label">First Name</Form.Label>
													<Form.Control 
														type="text"
														name="firstName" 
														value={formData.firstName} 
														onChange={handleInputChange} 
														required
														isInvalid={!!fieldErrors.firstName}
													className="register-input"
													/>
													{fieldErrors.firstName && (
														<Form.Control.Feedback type="invalid">
															{fieldErrors.firstName}
														</Form.Control.Feedback>
													)}
												</Form.Group>
											</Col>
											<Col xs={12} sm={6}>
												<Form.Group>
													<Form.Label className="register-label">Last Name</Form.Label>
													<Form.Control 
														type="text"
														name="lastName" 
														value={formData.lastName} 
														onChange={handleInputChange} 
														required
														isInvalid={!!fieldErrors.lastName}
														className="register-input"
													/>
													{fieldErrors.lastName && (
														<Form.Control.Feedback type="invalid">
															{fieldErrors.lastName}
														</Form.Control.Feedback>
													)}
												</Form.Group>
											</Col>
											<Col xs={12} sm={6}>
												<Form.Group>
													<Form.Label className="register-label">Email</Form.Label>
													<Form.Control 
														type="email"
														name="email" 
														value={formData.email} 
														onChange={handleInputChange} 
														required
														isInvalid={!!fieldErrors.email}
														className="register-input"
													/>
													{fieldErrors.email && (
														<Form.Control.Feedback type="invalid">
															{fieldErrors.email}
														</Form.Control.Feedback>
													)}
												</Form.Group>
											</Col>
											<Col xs={12} sm={6}>
												<Form.Group>
													<Form.Label className="register-label">Phone Number</Form.Label>
													<Form.Control 
														type="tel"
														name="phoneNumber" 
														value={formData.phoneNumber} 
														onChange={handleInputChange} 
														isInvalid={!!fieldErrors.phoneNumber}
														className="register-input"
													/>
													{fieldErrors.phoneNumber && (
														<Form.Control.Feedback type="invalid">
															{fieldErrors.phoneNumber}
														</Form.Control.Feedback>
													)}
												</Form.Group>
											</Col>
											<Col xs={12} sm={6}>
												<Form.Group>
													<Form.Label className="register-label">Username</Form.Label>
													<Form.Control 
														type="text"
														name="username" 
														value={formData.username} 
														onChange={handleInputChange} 
														required
														isInvalid={!!fieldErrors.username}
														className="register-input"
													/>
													{fieldErrors.username && (
														<Form.Control.Feedback type="invalid">
															{fieldErrors.username}
														</Form.Control.Feedback>
													)}
												</Form.Group>
											</Col>
											<Col xs={12} sm={6}>
												<Form.Group>
													<Form.Label className="register-label">Password</Form.Label>
													<Form.Control 
														type="password"
														name="password" 
														value={formData.password} 
														onChange={handleInputChange} 
														required
														isInvalid={!!fieldErrors.password}
														className="register-input"
													/>
													{fieldErrors.password && (
														<Form.Control.Feedback type="invalid">
															{fieldErrors.password}
														</Form.Control.Feedback>
													)}
												</Form.Group>
											</Col>											
										</Row>
										</div>
										
										<div className="d-flex flex-column gap-3 mt-4">

										<Form.Control 
														type="hidden"
														name="role" 
														value="user" 
													/>

											<button
												type="submit"
												disabled={isLoading}
												className="register-btn register-btn-primary py-3 fw-bold"
											>
												{isLoading ? (
													<>
														<Spinner animation="border" size="sm" className="me-2" />
														Creating Account...
													</>
												) : (
													'Create Account'
												)}
											</button>
											<p className="register-back-wrap mb-0 mt-2 text-center">
												<Link to="/login" className="register-back-link">
													Back to Login
												</Link>
											</p>
										</div>
									</Form>
									)}
						</div>
					</Col>
				</Row>
			</Container>
			<Footer />
		</div>
	);
};

export default Register;
